import { useCallback, useRef } from 'react';

interface EvgenEventWithNamespace<
    EvgenAnalytics = unknown,
    Namespace = unknown,
    EvgenEventParams = undefined
> {
    (evgen: EvgenAnalytics, namespace: Namespace, params: EvgenEventParams): void;
}

type CallbackParamsWithNamespace<Type> = Type extends EvgenEvent<any, any>
    ? {}
    : Type extends EvgenEventWithNamespace<any, infer Item, any>
    ? { namespace: Item }
    : {};

export type UseEventCallbackParamsWithoutBuilder<
    EventType extends (...agrs: any[]) => void,
    UiParams
> = {
    event: EventType;
    uiParams: UiParams;
} & CallbackParamsWithNamespace<EventType>;

interface EventParamsBuilder<
    UiParams,
    Dependencies,
    EvgenEventParams,
    CallbackParams extends any[] = []
> {
    ({
        dependencies,
        uiParams,
        callbackParams,
    }: {
        dependencies: Dependencies;
        uiParams: UiParams;
        callbackParams: CallbackParams;
    }): EvgenEventParams;
}

interface BuilderParam<
    UiParams,
    Dependencies,
    EvgenEventParams,
    CallbackParams extends any[] = []
> {
    eventParamsBuilder: EventParamsBuilder<
        UiParams,
        Dependencies,
        EvgenEventParams,
        CallbackParams
    >;
}

interface EvgenEvent<EvgenAnalytics = unknown, EvgenEventParams = undefined> {
    (evgen: EvgenAnalytics, params: EvgenEventParams): void;
}

export type EvgenEventParams<Type> = Type extends EvgenEvent<any, infer Item>
    ? Item
    : Type extends EvgenEventWithNamespace<any, any, infer Item>
    ? Item
    : never;

export type UseEventCallbackParams<
    EventType extends (...agrs: any[]) => void,
    UiParams,
    Dependencies,
    CallbackParams extends any[] = []
> = UseEventCallbackParamsWithoutBuilder<EventType, UiParams> &
    BuilderParam<UiParams, Dependencies, EvgenEventParams<EventType>, CallbackParams>;

function assertEvgen<EvgenAnalytics = unknown>(
    evgen: EvgenAnalytics
): asserts evgen is NonNullable<EvgenAnalytics> {
    if (!evgen) {
        throw new Error('Evgen was not provided');
    }
}

export function createUseEventCallback<Dependencies, EvgenAnalytics = unknown>({
    useDependencies,
    useEvgen,
}: {
    useDependencies: () => Dependencies;
    useEvgen: () => EvgenAnalytics;
}) {
    return function useEventCallback<
        EventType extends (evgen: NonNullable<EvgenAnalytics>, ...args: any[]) => void,
        UiParams,
        CallbackParams extends any[] = []
    >(useParams: UseEventCallbackParams<EventType, UiParams, Dependencies, CallbackParams>) {
        const { event, uiParams, eventParamsBuilder } = useParams;
        const namespace =
            'namespace' in useParams ? (useParams as { namespace: unknown }).namespace : undefined;
        const dependencies = useDependencies();
        const evgen = useEvgen();
        const uiParamsRef = useRef(uiParams);
        uiParamsRef.current = uiParams;

        return useCallback(
            (...callbackParams: CallbackParams) => {
                assertEvgen(evgen);
                if (namespace) {
                    event(
                        evgen,
                        namespace,
                        eventParamsBuilder({
                            dependencies,
                            uiParams: uiParamsRef.current,
                            callbackParams,
                        })
                    );
                } else {
                    event(
                        evgen,
                        eventParamsBuilder({
                            dependencies,
                            uiParams: uiParamsRef.current,
                            callbackParams,
                        })
                    );
                }
            },
            [event, eventParamsBuilder, dependencies, evgen, namespace]
        );
    };
}
